import React, { useState } from 'react';
import { ChatbotPersonality } from '../types/chatbot';
import Gallery from './Gallery';
import Badge from './ui/Badge';
import OptimizedVideo from './ui/OptimizedVideo';

interface ChatbotStats {
  likeCount: number;
  chatCount: number;
  isLiked: boolean;
  hasStartedChat: boolean;
}

interface ProfileContentProps {
  chatbot: ChatbotPersonality;
  chatbotStats?: ChatbotStats;
  onUpdateStats: (stats: Partial<ChatbotStats>) => void;
  onStartChat?: (chatbot: ChatbotPersonality) => void;
  showChatButton?: boolean;
  layout?: 'modal' | 'sidebar';
}

const ProfileContent: React.FC<ProfileContentProps> = ({
  chatbot,
  chatbotStats,
  onUpdateStats,
  onStartChat,
  showChatButton = false,
  layout = 'modal'
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const stats = chatbotStats || {
    likeCount: 0,
    chatCount: 0,
    isLiked: false,
    hasStartedChat: true,
  };

  const { likeCount, chatCount, isLiked, hasStartedChat } = stats;

  const handleLikeClick = () => {
    if (isLiked) {
      onUpdateStats({
        isLiked: false,
        likeCount: Math.max(0, likeCount - 1),
      });
    } else {
      onUpdateStats({
        isLiked: true,
        likeCount: likeCount + 1,
      });
    }
  };

  const handleChatClick = () => {
    if (onStartChat) {
      onUpdateStats({
        hasStartedChat: true,
        chatCount: chatCount + 1,
      });
      onStartChat(chatbot);
    }
  };

  // Handle media assets (both images and videos)
  const galleryAssets = (chatbot as any).galleryAssets || [];
  const images = chatbot.gallery || [];
  const mediaAssets = galleryAssets.length > 0 ? galleryAssets : images.map(url => ({ url, type: 'image' }));
  const hasMedia = mediaAssets.length > 0;

  const nextImage = () => {
    if (hasMedia) {
      setCurrentImageIndex((prev) => (prev + 1) % mediaAssets.length);
    }
  };

  const prevImage = () => {
    if (hasMedia) {
      setCurrentImageIndex((prev) => (prev - 1 + mediaAssets.length) % mediaAssets.length);
    }
  };

  // Render media content (image or video)
  const renderMediaContent = (index: number) => {
    if (!hasMedia) return null;

    const asset = mediaAssets[index];
    const isVideo = asset.type === 'video' || asset.url?.includes('.mp4') || asset.url?.includes('.webm');

    if (isVideo) {
      return (
        <OptimizedVideo
          src={asset.url}
          poster={asset.poster}
          className="w-full h-full"
          autoPlay={true}
          muted={true}
          loop={true}
          controls={false}
          priority={true}
          onLoad={() => {
            console.log('🎬 Profile OptimizedVideo loaded');
          }}
          onError={() => {
            console.error('🚫 Profile OptimizedVideo failed');
          }}
        />
      );
    } else {
      return (
        <img
          src={asset.url}
          alt={`${chatbot.name} ${index + 1}`}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      );
    }
  };

  const renderGallerySection = () => {
    if (!hasMedia) return null;

    return (
      <div className={`relative ${layout === 'sidebar' ? 'aspect-video' : 'aspect-square'} overflow-hidden`} style={{ backgroundColor: 'transparent' }}>
        {renderMediaContent(currentImageIndex)}

        {/* Navigation arrows - NO PERFORMANCE KILLERS */}
        {mediaAssets.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center text-white z-10"
              style={{ transition: 'none' }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center text-white z-10"
              style={{ transition: 'none' }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Media counter - NO BACKDROP BLUR */}
            <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full z-10">
              {currentImageIndex + 1} / {mediaAssets.length}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderProfileInfo = () => (
    <div className="space-y-4">
      {/* Header with name and age - only show in sidebar layout */}
      {layout === 'sidebar' && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-white">
              {chatbot.name.toUpperCase()}
            </h1>
            <span className="text-lg font-semibold text-accent-pink">
              {chatbot.age || '?'}
            </span>
          </div>
          {/* Status and Actions */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <Badge variant="success" className="text-xs hidden sm:flex">
              <div className="w-2 h-2 bg-accent-purple rounded-full mr-2 animate-pulse"></div>
              Online
            </Badge>
          </div>
        </div>
      )}

      {/* Stats Row - only show in sidebar layout */}
      {layout === 'sidebar' && (
        <div className="flex items-center justify-between py-3 border-y border-dark-600">
          <div className="text-center">
            <div className="text-lg font-semibold text-white">{likeCount}</div>
            <div className="text-xs text-dark-300">Likes</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-white">{chatCount}</div>
            <div className="text-xs text-dark-300">Chats</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-white">{mediaAssets.length}</div>
            <div className="text-xs text-dark-300">Photos</div>
          </div>
        </div>
      )}

      {/* Appearance Section */}
      <div>
        <h3 className="text-dark-300 text-sm font-medium mb-2 flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          APPEARANCE
        </h3>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-dark-700 rounded-lg flex items-center justify-center">
              <span className="text-xs">👁️</span>
            </div>
            <div>
              <div className="text-xs text-dark-400">Eye Color</div>
              <div className="text-xs text-white">
                {(chatbot as any).attributes?.physical?.eye_color || 'Unknown'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-dark-700 rounded-lg flex items-center justify-center">
              <span className="text-xs">💇</span>
            </div>
            <div>
              <div className="text-xs text-dark-400">Hair Color</div>
              <div className="text-xs text-white">
                {(chatbot as any).attributes?.physical?.hair_color || 'Unknown'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-dark-700 rounded-lg flex items-center justify-center">
              <span className="text-xs">💪</span>
            </div>
            <div>
              <div className="text-xs text-dark-400">Body Type</div>
              <div className="text-xs text-white">
                {(chatbot as any).attributes?.physical?.body_type || 'Unknown'}
              </div>
            </div>
          </div>
          {(chatbot as any).attributes?.physical?.hair_style && (
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-dark-700 rounded-lg flex items-center justify-center">
                <span className="text-xs">✂️</span>
              </div>
              <div>
                <div className="text-xs text-dark-400">Hair Style</div>
                <div className="text-xs text-white">
                  {(chatbot as any).attributes.physical.hair_style}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Character Section */}
      <div>
        <h3 className="text-dark-300 text-sm font-medium mb-3 flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          CHARACTER
        </h3>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-dark-700 rounded-lg flex items-center justify-center">
              <span className="text-xs">🎂</span>
            </div>
            <div>
              <div className="text-xs text-dark-400">Age</div>
              <div className="text-xs text-white">{chatbot.age || 'Unknown'}</div>
            </div>
          </div>
          {(chatbot as any).attributes?.personal?.occupation && (
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-dark-700 rounded-lg flex items-center justify-center">
                <span className="text-xs">💼</span>
              </div>
              <div>
                <div className="text-xs text-dark-400">Occupation</div>
                <div className="text-xs text-white">{(chatbot as any).attributes.personal.occupation}</div>
              </div>
            </div>
          )}
          {(chatbot as any).attributes?.personal?.hobby && (
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-dark-700 rounded-lg flex items-center justify-center">
                <span className="text-xs">🎯</span>
              </div>
              <div>
                <div className="text-xs text-dark-400">Hobby</div>
                <div className="text-xs text-white">{(chatbot as any).attributes.personal.hobby}</div>
              </div>
            </div>
          )}
          {(chatbot as any).attributes?.physical?.ethnicity && (
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-dark-700 rounded-lg flex items-center justify-center">
                <span className="text-xs">🌍</span>
              </div>
              <div>
                <div className="text-xs text-dark-400">Ethnicity</div>
                <div className="text-xs text-white">{(chatbot as any).attributes.physical.ethnicity}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Personality Section */}
      {chatbot.personality && chatbot.personality !== '{}' && chatbot.personality !== 'null' && (
        <div>
          <h3 className="text-dark-300 text-sm font-medium mb-3 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            💭 PERSONALITY
          </h3>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-dark-700 rounded-lg flex items-center justify-center">
              <span className="text-xs">✨</span>
            </div>
            <div>
              <div className="text-xs text-dark-400">Traits</div>
              <div className="text-xs text-white">
                {typeof chatbot.personality === 'string' ? chatbot.personality : JSON.stringify(chatbot.personality)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* About Section */}
      <div>
        <h3 className="text-dark-300 text-sm font-medium mb-3 flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          ABOUT
        </h3>
        <p className="text-sm text-dark-200 leading-relaxed">
          {chatbot.description || `${chatbot.name} is a charming AI companion who embodies ${chatbot.personality?.toLowerCase()} qualities. She's designed to provide engaging conversations and meaningful connections through her unique personality and approach to relationships.`}
        </p>
      </div>

      {/* Gallery Section */}
      {mediaAssets.length > 0 && (
        <div>
          <h3 className="text-dark-300 text-sm font-medium mb-2 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
            </svg>
            GALLERY
          </h3>
          <div className="space-y-3">
            <Gallery
              assets={mediaAssets}
              gridCols={layout === 'sidebar' ? 3 : 4}
              separateMediaTypes={true}
              className="space-y-3"
              initialLoadCount={5}
              loadMoreCount={5}
            />
          </div>
        </div>
      )}

      {/* Action Buttons - only show in sidebar layout */}
      {layout === 'sidebar' && (
        <div className="space-y-3 pt-4 border-t border-dark-600">
          {showChatButton && (
            <button
              onClick={handleChatClick}
              className="w-full py-3 bg-gradient-to-r from-accent-purple to-accent-pink hover:shadow-lg hover:shadow-accent-purple/25 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {hasStartedChat ? 'Continue Chat' : 'Start Chat'}
            </button>
          )}

          <button
            onClick={handleLikeClick}
            className={`w-full py-2 px-4 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 ${isLiked
              ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
              : 'bg-dark-700 text-dark-300 border border-dark-600 hover:bg-dark-600 hover:text-white'
              }`}
          >
            <svg className="w-4 h-4" fill={isLiked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            {likeCount}
          </button>

          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-white">Customize chat</h4>
            <button className="w-full p-3 bg-dark-700 hover:bg-dark-600 rounded-lg text-left text-sm text-white transition-all duration-300 flex items-center gap-3">
              <span className="text-lg">🎨</span>
              <span>Change theme</span>
            </button>
            <button className="w-full p-3 bg-dark-700 hover:bg-dark-600 rounded-lg text-left text-sm text-white transition-all duration-300 flex items-center gap-3">
              <span className="text-lg">😊</span>
              <span>Change emoji</span>
            </button>
            <button className="w-full p-3 bg-dark-700 hover:bg-dark-600 rounded-lg text-left text-sm text-white transition-all duration-300 flex items-center gap-3">
              <span className="text-lg">🔇</span>
              <span>Mute notifications</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (layout === 'sidebar') {
    return (
      <div className="space-y-0">
        {renderGallerySection()}
        <div className="p-4">
          {renderProfileInfo()}
        </div>
      </div>
    );
  }

  // Modal layout - Gallery is already included in renderProfileInfo()
  return (
    <div className="space-y-4">
      {renderProfileInfo()}
    </div>
  );
};

export default ProfileContent;